import * as React from 'react';
import { Briefcase, Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api-client';

export interface ProcessOrCaseValue {
  caseId: string | null;
  clientId: string | null;
  freeText: string;
}

export interface ProcessOrCaseSelectProps {
  value: ProcessOrCaseValue;
  onChange: (next: ProcessOrCaseValue) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Quando definido, o campo fica read-only mostrando o vínculo já existente. */
  readOnly?: boolean;
}

/** Formata o número CNJ para exibição curta (primeiros 25 chars + ellipsis). */
function shortCnj(numeroCnj: string | null): string {
  if (!numeroCnj) return '';
  return numeroCnj.length > 30 ? `${numeroCnj.slice(0, 27)}…` : numeroCnj;
}

/**
 * Combobox de busca dual — procura processos e clientes em paralelo e permite
 * selecionar um ou usar texto livre como fallback.
 *
 * Comportamento do input direto:
 * - Se o usuário digitar e selecionar uma sugestão → vincula caseId/clientId
 * - Se o usuário digitar e sair sem selecionar (blur/Tab) → vira freeText automaticamente
 * - O botão "Usar como texto livre" no rodapé do popup continua disponível como atalho
 */
export function ProcessOrCaseSelect({
  value,
  onChange,
  placeholder = 'Processo ou caso',
  disabled = false,
  readOnly = false,
}: ProcessOrCaseSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Quando vinculado, o input mostra o label do vínculo e fica readonly até X
  const isLinked = !!(value.caseId || value.clientId);

  // --- queries ----------------------------------------------------------------
  const enabled = open && query.trim().length >= 1;

  const { data: casesData, isFetching: fetchingCases } = useQuery({
    queryKey: ['process-or-case-select', 'cases', query],
    queryFn: () => apiClient.casesList({ search: query.trim(), pageSize: 5 }),
    enabled,
    staleTime: 30_000,
  });

  const { data: clientsData, isFetching: fetchingClients } = useQuery({
    queryKey: ['process-or-case-select', 'clients', query],
    queryFn: () => apiClient.clientsList({ search: query.trim(), pageSize: 5 }),
    enabled,
    staleTime: 30_000,
  });

  const cases = casesData?.items ?? [];
  const clients = clientsData?.items ?? [];
  const loading = fetchingCases || fetchingClients;
  const hasResults = cases.length > 0 || clients.length > 0;

  // Quando há vínculo mas ainda não carregamos o item (modo edição), busca pelo ID
  const { data: preloadedCase } = useQuery({
    queryKey: ['case-preload', value.caseId],
    queryFn: () => apiClient.casesGet(value.caseId!),
    enabled: !!value.caseId && !casesData?.items.some((i) => i.id === value.caseId),
    staleTime: 60_000,
  });

  const { data: preloadedClient } = useQuery({
    queryKey: ['client-preload', value.clientId],
    queryFn: () => apiClient.clientsGet(value.clientId!),
    enabled: !!value.clientId && !clientsData?.items.some((i) => i.id === value.clientId),
    staleTime: 60_000,
  });

  function resolvedTriggerLabel(): string {
    if (value.caseId) {
      const fromList = casesData?.items.find((i) => i.id === value.caseId);
      if (fromList) {
        const cnj = shortCnj(fromList.numeroCnj);
        return cnj ? `Proc. ${cnj} — ${fromList.mainPartyNames}` : `Proc. — ${fromList.mainPartyNames}`;
      }
      if (preloadedCase) {
        const cnj = shortCnj(preloadedCase.numeroCnj);
        const mainParty = preloadedCase.parties?.find((p) => p.isMain)?.clientName ?? '';
        return cnj ? `Proc. ${cnj}${mainParty ? ` — ${mainParty}` : ''}` : `Proc.${mainParty ? ` — ${mainParty}` : ''}`;
      }
      return 'Carregando processo…';
    }
    if (value.clientId) {
      const fromList = clientsData?.items.find((i) => i.id === value.clientId);
      const item = fromList ?? preloadedClient;
      if (item) return item.name;
      return 'Carregando cliente…';
    }
    return '';
  }

  const linkedLabel = resolvedTriggerLabel();

  // --- handlers ---------------------------------------------------------------
  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value;
    setQuery(v);
    if (!open) setOpen(true);
  }

  function handleInputFocus() {
    if (!isLinked && !readOnly && !disabled) {
      setOpen(true);
    }
  }

  /**
   * Ao sair do input sem ter selecionado um item vinculado:
   * - se havia texto digitado → vira freeText automaticamente
   * - se não havia texto → limpa
   */
  function handleInputBlur() {
    // Pequeno delay para permitir que o onMouseDown do item da lista execute antes
    setTimeout(() => {
      if (open) {
        const trimmed = query.trim();
        if (trimmed && !isLinked) {
          onChange({ caseId: null, clientId: null, freeText: trimmed });
        }
        setOpen(false);
        setQuery('');
      }
    }, 150);
  }

  function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  }

  function selectCase(id: string) {
    onChange({ caseId: id, clientId: null, freeText: '' });
    setOpen(false);
    setQuery('');
  }

  function selectClient(id: string) {
    onChange({ caseId: null, clientId: id, freeText: '' });
    setOpen(false);
    setQuery('');
  }

  function useFreeText() {
    onChange({ caseId: null, clientId: null, freeText: query.trim() });
    setOpen(false);
    setQuery('');
  }

  function clearValue() {
    onChange({ caseId: null, clientId: null, freeText: '' });
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 10);
  }

  // Fecha ao clicar fora
  React.useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // --- render -----------------------------------------------------------------
  return (
    <div ref={wrapRef} className="relative">
      {/* Wrapper estilo input com ícone + campo editável + botão X */}
      <div
        className={cn(
          'flex h-9 w-full items-center gap-2 rounded-xl border bg-[var(--surface)] px-2.5 text-sm transition',
          'border-[var(--border)] text-[var(--fg)]',
          open && 'ring-2 ring-[var(--accent)]',
          (disabled || readOnly) && 'cursor-not-allowed opacity-50',
        )}
      >
        {isLinked ? (
          <Briefcase className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" />
        ) : (
          <Search className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)]" />
        )}

        {/* Quando vinculado: mostra badge readonly + X. Quando livre: input editável */}
        {isLinked ? (
          <span className="flex-1 truncate text-sm text-[var(--fg)]">{linkedLabel}</span>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query || (value.freeText && !open ? value.freeText : query)}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            disabled={disabled || readOnly}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-sm text-[var(--fg)] outline-none placeholder:text-[var(--fg-muted)] disabled:cursor-not-allowed"
          />
        )}

        {(isLinked || value.freeText) && !readOnly && !disabled && (
          <button
            type="button"
            tabIndex={0}
            onMouseDown={(e) => {
              e.preventDefault();
              clearValue();
            }}
            className="ml-1 rounded p-0.5 hover:bg-[color-mix(in_oklch,var(--fg)_10%,transparent)]"
            aria-label="Remover vínculo"
          >
            <X className="h-3 w-3 text-[var(--fg-muted)]" />
          </button>
        )}
      </div>

      {/* Popup de sugestões */}
      {open && !isLinked && (
        <div
          className={cn(
            'absolute left-0 right-0 z-50 mt-1 rounded-xl border shadow-lg',
            'border-[var(--popover-border,var(--card-border))]',
            'backdrop-blur-[var(--blur,0px)]',
          )}
          style={{ background: 'var(--popover-bg, var(--card-bg))', boxShadow: 'var(--shadow-card)' }}
        >
          <div className="max-h-64 overflow-y-auto py-1">
            {loading && query.trim().length >= 1 && (
              <p className="px-3 py-2 text-xs text-[var(--fg-muted)]">Buscando…</p>
            )}

            {/* Seção Processos */}
            {cases.length > 0 && (
              <>
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                  Processos
                </p>
                {cases.map((c) => {
                  const cnj = shortCnj(c.numeroCnj);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectCase(c.id);
                      }}
                      className="flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm transition hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
                    >
                      <span className="truncate font-medium text-[var(--fg)]">
                        {cnj ? `Proc. ${cnj}` : 'Processo s/n'}
                      </span>
                      <span className="truncate text-xs text-[var(--fg-muted)]">
                        {c.mainPartyNames}
                      </span>
                    </button>
                  );
                })}
              </>
            )}

            {/* Seção Clientes */}
            {clients.length > 0 && (
              <>
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--fg-muted)]">
                  Clientes
                </p>
                {clients.map((cl) => (
                  <button
                    key={cl.id}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectClient(cl.id);
                    }}
                    className="flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm transition hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
                  >
                    <span className="truncate font-medium text-[var(--fg)]">{cl.name}</span>
                    {cl.document && (
                      <span className="text-xs text-[var(--fg-muted)]">{cl.document}</span>
                    )}
                  </button>
                ))}
              </>
            )}

            {/* Empty state */}
            {query.trim().length >= 1 && !loading && !hasResults && (
              <p className="px-3 py-2 text-xs text-[var(--fg-muted)]">
                Nenhum processo ou cliente encontrado.
              </p>
            )}

            {query.trim().length === 0 && (
              <p className="px-3 py-2 text-xs text-[var(--fg-muted)]">
                Digite para buscar processos ou clientes.
              </p>
            )}

            {/* Botão texto livre (atalho — não é mais obrigatório) */}
            {query.trim().length >= 1 && (
              <div className="border-t border-[var(--border)] px-3 py-2">
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    useFreeText();
                  }}
                  className="text-xs text-[var(--fg-muted)] hover:text-[var(--fg)] transition"
                >
                  Usar como texto livre: &ldquo;{query.trim()}&rdquo;
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
